import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { useWindowStore } from '@/wm/store';
import { useShutdownStore } from '@/shell/Shutdown/store';
import { rightColumn } from '@/config/startMenu';
import { site } from '@/config/site';
import type { AppArgs } from '@/wm/types';
import type { AppManifest } from '@/apps/types';
import { AllProgramsFlyout } from './AllProgramsFlyout';
import { useStartMenuItems } from './useStartMenuItems';
import { showContextMenu } from '@/shell/ContextMenu/ContextMenu';
import { msgbox, readOnlyDisk } from '@/shell/ContextMenu/common';
import { separator } from '@/shell/Menus/types';
import type { MenuItem } from '@/shell/Menus/types';
import css from './StartMenu.module.css';

export interface StartMenuProps {
  open: boolean;
  onClose: () => void;
  /**
   * The Start button. A click on it must not count as an outside click, or the
   * button's own toggle would immediately re-open what we just closed.
   */
  anchorRef: RefObject<HTMLElement | null>;
}

export function StartMenu({ open, onClose, anchorRef }: StartMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  /** The All Programs row and its flyout — the one region hovering must not close. */
  const wrapRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const [programsOpen, setProgramsOpen] = useState(false);
  const { pinned, recent } = useStartMenuItems();

  // A dismissed menu must not come back with its flyout still hanging out.
  useEffect(() => {
    if (!open) setProgramsOpen(false);
  }, [open]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  /**
   * Close, then hand the keyboard back to the Start button so the menu never
   * strands focus on `document.body`. `immediate` is for Escape, where nothing
   * else is competing; every other route waits a frame and stands down if a
   * launched window or dialog claimed focus for itself.
   */
  const close = useCallback(
    (immediate = false) => {
      onClose();
      const target = anchorRef.current;
      if (!target) return;
      if (immediate) {
        target.focus({ preventScroll: true });
        return;
      }
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body) return;
        if (target.isConnected) target.focus({ preventScroll: true });
      });
    },
    [onClose, anchorRef],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The open menu owns Escape; windows underneath must not also react.
      e.stopPropagation();
      close(true);
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      close();
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, close, anchorRef]);

  useEffect(() => {
    if (open) menuRef.current?.focus();
  }, [open]);

  const launch = useCallback(
    (appId: string, args?: AppArgs) => {
      useWindowStore.getState().open(appId, args);
      close();
    },
    [close],
  );

  // Both footer buttons hand over to the shutdown overlay, which owns the
  // panel, the fade and the screen that is left behind.
  const logOff = useCallback(() => {
    useShutdownStore.getState().begin('logoff');
    close();
  }, [close]);

  const turnOff = useCallback(() => {
    useShutdownStore.getState().begin('shutdown');
    close();
  }, [close]);

  /**
   * What closes the All Programs flyout.
   *
   * Not leaving it — that was the bug. A flyout that closes on `mouseleave`
   * dies the instant anything unmounts under the pointer, and it punishes the
   * gap between where the panel is drawn and where its owner's box happens to
   * be. `DropdownMenu` holds the opposite invariant for the application menus:
   * leaving a popup never closes anything, and closing is always a positive
   * act. This is that act — the reader has settled on something else in the
   * menu — with the flyout's own region exempt.
   *
   * `mouseover` rather than `mouseenter` because it bubbles, so this one
   * handler covers every row, rule, gap and the footer, instead of each of
   * them having to remember to close the flyout itself.
   *
   * Wandering off to the desktop still closes nothing. The Start menu already
   * dismisses on an outside pointerdown, Escape and blur, which is how a
   * Windows menu has always been dismissed.
   */
  const onMenuOver = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!programsOpen) return;
    const target = event.target;
    if (target instanceof Node && wrapRef.current?.contains(target)) return;
    setProgramsOpen(false);
  };

  if (!open) return null;

  /**
   * A program's own menu, which is where XP kept Pin to Start Menu. The two
   * rows that pin and unpin are drawn and greyed rather than left out: this
   * menu is assembled from the installed programs, so the list is not the
   * visitor's to rearrange.
   */
  const programMenu = (app: AppManifest, isPinned: boolean): MenuItem[] => [
    { label: '&Open', defaultVerb: true, onSelect: () => launch(app.id) },
    separator,
    { label: 'Pin to Start men&u', checked: isPinned, disabled: true },
    { label: '&Remove from This List', disabled: true },
    separator,
    { label: 'Create &Shortcut', onSelect: () => readOnlyDisk('shortcut') },
    separator,
    {
      label: 'P&roperties',
      onSelect: () =>
        msgbox({
          title: `${app.name} Properties`,
          icon: 'info',
          text: app.name,
          detail: ['Type: Application', `Location: C:\\Program Files\\${app.name}`].join('\n'),
        }),
    },
  ];

  const appRow = (app: AppManifest, bold: boolean) => (
    <li key={app.id} role="none">
      <button
        type="button"
        role="menuitem"
        className={css.row}
        onClick={() => launch(app.id)}
        onContextMenu={(event) => showContextMenu(event, programMenu(app, bold))}
      >
        <Icon src={app.icon} size={24} />
        <span className={clsx(css.rowLabel, bold && css.labelBold)}>{app.name}</span>
      </button>
    </li>
  );

  return (
    <div ref={menuRef} className={css.menu} tabIndex={-1} onMouseOver={onMenuOver}>
      <div className={css.header}>
        <Icon src="/assets/ui/user-tile.svg" size={44} className={css.avatar} />
        <span className={css.userName}>{site.userName}</span>
      </div>

      <div className={css.columns}>
        <div className={css.left}>
          <ul className={css.pinnedList} role="menu" aria-label="Pinned programs">
            {pinned.map((app) => appRow(app, true))}
          </ul>

          {pinned.length > 0 && recent.length > 0 ? <hr className={css.rule} /> : null}

          <ul className={css.recentList} role="menu" aria-label="Programs">
            {recent.map((app) => appRow(app, false))}
          </ul>

          <div ref={wrapRef} className={css.allProgramsWrap}>
            <hr className={css.rule} />
            <button
              type="button"
              className={clsx(css.row, css.allPrograms, programsOpen && css.rowOpen)}
              aria-haspopup="true"
              aria-expanded={programsOpen}
              onMouseEnter={() => setProgramsOpen(true)}
              /*
               * Open, not toggle. Hovering has already opened it by the time a
               * mouse gets here, so a toggle would shut it again — and on a
               * touch device, where the browser synthesises mouseenter before
               * the click, a toggle means All Programs can never be opened by
               * tapping it at all.
               */
              onClick={() => setProgramsOpen(true)}
            >
              <span className={css.rowLabel}>All Programs</span>
              <span className={css.chevron} aria-hidden="true" />
            </button>
            {programsOpen ? <AllProgramsFlyout onLaunch={launch} /> : null}
          </div>
        </div>

        <ul className={clsx(css.right, css.rightList)} role="menu" aria-label="Places">
          {rightColumn.map((entry) => (
            <Fragment key={`${entry.appId}:${entry.label}`}>
              {entry.separatorBefore ? <li className={css.rule} role="separator" /> : null}
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={css.row}
                  onClick={() => launch(entry.appId, entry.args)}
                >
                  <Icon src={entry.icon} size={24} />
                  <span
                    className={clsx(
                      css.rowLabel,
                      entry.small ? css.labelSmall : css.labelBold,
                    )}
                  >
                    {entry.label}
                  </span>
                </button>
              </li>
            </Fragment>
          ))}
        </ul>
      </div>

      <div className={css.footer}>
        <button type="button" className={css.footerButton} onClick={logOff}>
          <Icon src="/assets/icons/logoff.svg" size={16} />
          Log Off
        </button>
        <button type="button" className={css.footerButton} onClick={turnOff}>
          <Icon src="/assets/icons/shutdown.svg" size={16} />
          Turn Off Computer
        </button>
      </div>
    </div>
  );
}
